import { Routes } from '@angular/router';

import { Home } from '@features/home/home';
import { NotFound } from '@features/not-found/not-found';
import { detectPreferredLanguage } from '@core/i18n/language';
import { languageGuard } from '@core/i18n/language.guard';

export const routes: Routes = [
    {
        // No language in the URL → redirect to the saved/preferred language (`/en`, `/de`). The
        // preference is read from the cookie during SSR and localStorage on the client.
        path: '',
        pathMatch: 'full',
        redirectTo: (): string => `/${detectPreferredLanguage()}`,
    },
    {
        path: ':lang',
        canActivate: [languageGuard],
        children: [
            {
                // No `title` here — and none on any route. `SeoService` owns the document title
                // (CMS `metaTitle`, else the `seo.default.title` i18n key), and it is already
                // correct in the server-rendered HTML. Angular's `TitleStrategy` re-applies a
                // route `title` on every `NavigationEnd`, including the client's initial
                // hydration navigation, so a static one overwrote the SSR title for a frame
                // before `SeoService` restored it — a visible flash of an untranslated English
                // title on `/de`. See `app.routes.spec.ts`.
                path: '',
                pathMatch: 'full',
                component: Home,
            },
            {
                // Dynamic CMS page resolved by slug (an `article`). Must stay last among the
                // language children so any specific sibling routes win over the `:slug` param.
                path: ':slug',
                loadComponent: (): Promise<
                    typeof import('@features/dynamic-page/dynamic-page').DynamicPage
                > =>
                    import('@features/dynamic-page/dynamic-page').then(
                        (
                            m: typeof import('@features/dynamic-page/dynamic-page'),
                        ): typeof m.DynamicPage => m.DynamicPage,
                    ),
            },
        ],
    },
    {
        // Anything else is genuinely missing: render the 404 page, which sets a real 404 status on
        // the SSR response. It must NOT redirect to the home page — that returns HTTP 200 for every
        // URL a crawler probes (a soft 404), which is how `/llms.txt` came back as a landing page.
        // A single unknown segment never reaches here: the `:lang` guard prefixes it with the
        // active language first, so `/impressum` gets a chance to resolve as a CMS slug.
        path: '**',
        component: NotFound,
    },
];
